import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import crossIcon from "../../../../assets/cross.svg";
import onboardingImage from "../../../../assets/images/onboarding/onboarding-steps.png";
import Button from "../../../../components/button";
import { useMetrics } from "../../../../metrics";
import { OnboardingTypes } from "../../../infrastructure/hooks/useOnboarding/useOnboarding.types";
import useOnboardingMarkAsSeen from "../../../infrastructure/hooks/useOnboardingMarkAsSeen";

const OnboardingModal = () => {
  const { t } = useTranslation();
  const { mutate: markAsSeen } = useOnboardingMarkAsSeen();
  const { trackAction, pageView } = useMetrics();

  useEffect(() => {
    pageView("Onboarding Steps Modal");
  }, []);

  const handleButtonClick = () => {
    trackAction.click({
      screenName: "Onboarding Steps Modal",
      componentName: "Button Click",
    });
    markAsSeen(OnboardingTypes.ONBOARDING_STEPS);
  };

  const handleCloseClick = () => {
    trackAction.click({
      screenName: "Onboarding Steps Modal",
      componentName: "Close Click",
    });
    markAsSeen(OnboardingTypes.ONBOARDING_STEPS);
  };

  return (
    <div className="fixed inset-0 bg-gray-500 z-10 bg-opacity-50 flex justify-center items-center">
      <div
        role="dialog"
        className="flex flex-col gap-8 bg-white w-[936px] h-[574px] rounded-xl p-8 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 shadow-[1px_2px_20px_0_rgba(151,151,151,0.50)]"
      >
        <div className="flex h-[412px]">
          <div className="flex ml-4 flex-col gap-6">
            <h1 className="text-[28px] font-semibold leading-10">
              {t("onboarding-steps.modal.title")}
            </h1>
            <div className="flex row gap-2 ">
              <div className="flex w-8 h-8 p-1 justify-center items-center rounded-full border border-secondary-main fill-primary-main">
                <svg
                  data-testid="check-icon"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g id="Icons/check">
                    <path
                      id="Vector"
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M20.7071 5.29289C21.0976 5.68342 21.0976 6.31658 20.7071 6.70711L9.70711 17.7071C9.51957 17.8946 9.26522 18 9 18C8.73478 18 8.48043 17.8946 8.29289 17.7071L3.29289 12.7071C2.90237 12.3166 2.90237 11.6834 3.29289 11.2929C3.68342 10.9024 4.31658 10.9024 4.70711 11.2929L9 15.5858L19.2929 5.29289C19.6834 4.90237 20.3166 4.90237 20.7071 5.29289Z"
                      fill="#1B51DC"
                    />
                  </g>
                </svg>
              </div>
              <div className="">
                <p className="text-gray-900 text-base font-medium leading-6 mb-1">
                  {t("onboarding-steps.modal.step-1.title")}
                </p>
                <p className="text-gray-700 text-sm font-normal leading-5">
                  {t("onboarding-steps.modal.step-1.description")}
                </p>
              </div>
            </div>
            <div className="flex row gap-2">
              <div className="flex w-8 h-8 p-1 justify-center items-center rounded-full border border-secondary-main fill-primary-main">
                <svg
                  data-testid="check-icon"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g id="Icons/check">
                    <path
                      id="Vector"
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M20.7071 5.29289C21.0976 5.68342 21.0976 6.31658 20.7071 6.70711L9.70711 17.7071C9.51957 17.8946 9.26522 18 9 18C8.73478 18 8.48043 17.8946 8.29289 17.7071L3.29289 12.7071C2.90237 12.3166 2.90237 11.6834 3.29289 11.2929C3.68342 10.9024 4.31658 10.9024 4.70711 11.2929L9 15.5858L19.2929 5.29289C19.6834 4.90237 20.3166 4.90237 20.7071 5.29289Z"
                      fill="#1B51DC"
                    />
                  </g>
                </svg>
              </div>
              <div className="">
                <p className="text-gray-900 text-base font-medium leading-6 mb-1">
                  {t("onboarding-steps.modal.step-2.title")}
                </p>
                <p className="text-gray-700 text-sm font-normal leading-5">
                  {t("onboarding-steps.modal.step-2.description")}
                </p>
              </div>
            </div>
            <div className="flex row gap-2">
              <div className="flex w-8 h-8 p-1 justify-center items-center rounded-full bg-secondary-main text-white">
                3
              </div>
              <div className="">
                <p className="text-gray-900 text-base font-medium leading-6 mb-1">
                  {t("onboarding-steps.modal.step-3.title")}
                </p>
                <p className="text-gray-700 text-sm font-normal leading-5">
                  {t("onboarding-steps.modal.step-3.description")}
                </p>
              </div>
            </div>
            <div className="flex row gap-2">
              <div className="flex w-8 h-8 p-1 justify-center items-center rounded-full bg-secondary-main text-white">
                4
              </div>
              <div className="">
                <p className="text-gray-900 text-base font-medium leading-6 mb-1">
                  {t("onboarding-steps.modal.step-4.title")}
                </p>
                <p className="text-gray-700 text-sm font-normal leading-5">
                  {t("onboarding-steps.modal.step-4.description")}
                </p>
              </div>
            </div>
          </div>
          <img
            src={onboardingImage}
            alt=""
            className="w-[344px] h-[315px] self-center object-cover"
          />
        </div>
        <div className="flex flex-col items-center gap-2">
          <div className="flex h-12 min-w-[192px] max-w-[192px] justify-center">
            <Button
              onClick={handleButtonClick}
              text={t("onboarding-steps.modal.button")}
            />
          </div>
          <p className="text-gray-700 text-center text-xs">
            {t("onboarding-steps.modal.info")}
          </p>
        </div>
        <button
          onClick={handleCloseClick}
          aria-label="Cerrar"
          className="absolute right-0 top-0 m-8"
        >
          <img src={crossIcon} alt="" className="w-6" />
        </button>
      </div>
    </div>
  );
};

export { OnboardingModal };
